import { AddChecklistResponse, UpdateChecklistResponse } from "./ChecklistResponses"
import HttpClient from "../../../core/api/httpClient"
import { ChecklistRequest } from "./ChecklistRequests"
import { PagedList } from "../../../core/api/pagedList"
import { Checklist, ChecklistScheduleDto, ScheduleType } from "../model/ChecklistDtos"
import { toast } from "react-toastify"
import { t } from "i18next"

const baseUrl = '/api/checklists'
const getUrl = (searchParams: URLSearchParams, odataQueryParameters?: string): string => (`${baseUrl}?${searchParams}&${odataQueryParameters ?? ''}`)
const getByIdUrl = (id: string): string => `${baseUrl}/${id}`
const getByIdWithSchedulesUrl = (id: string): string => `${baseUrl}/${id}?fetch-schedules=true`
const deleteChecklistUrl = (id: number): string => (`${baseUrl}/${id}`)
const getScheduleUrl = (idChecklist: number, idChecklistSchedule: number): string => `${baseUrl}/schedule/${idChecklist}/${idChecklistSchedule}`
const scheduleChecklistUrl = (`${baseUrl}/schedule`)
const deleteScheduleUrl = (id: string): string => (`${baseUrl}/${id}/schedule`)

export default class ChecklistsApi {

    static async getChecklists(fetchSchedules: boolean, odataQueryParameters?: string): Promise<PagedList<Checklist>> {

        const queryParams = new URLSearchParams({
            fetchSchedules: fetchSchedules.toString(),
        })

        const response = await HttpClient.get(getUrl(queryParams, odataQueryParameters))
        return await response.json()
    }

    static async getChecklistById(id: string, fetchSchedules: boolean): Promise<Checklist> {
        const url = fetchSchedules ? getByIdWithSchedulesUrl(id) : getByIdUrl(id)
        const response = await HttpClient.get(url)
        return await response.json()
    }

    static async updateChecklist(id: string, checklist: ChecklistRequest): Promise<UpdateChecklistResponse> {
        try {
            const response = await HttpClient.put(getByIdUrl(id), {
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(checklist),
            })

            if (response.ok) {
                const data = await response.json()
                return {
                    status: response.status,
                    data: data,
                    id: data.id,
                    succeeded: true,
                }
            } else {
                const errorText = await response.text()
                return {
                    status: response.status,
                    errorMessage: errorText || `Request failed with status ${response.status}`,
                    succeeded: false,
                }
            }
        } catch (error) {
            throw new Error("An unexpected error occurred while updating the checklist.")
        }
    }

    static async addChecklist(checklist: ChecklistRequest): Promise<AddChecklistResponse> {
        try {
            const response = await HttpClient.post(baseUrl, {
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(checklist),
            })
            if (response.ok) {
                const data = await response.json()
                return {
                    status: response.status,
                    id: data.id,
                    succeeded: true,
                }
            } else {
                const errorText = await response.text()
                return {
                    status: response.status,
                    errorMessage: errorText || `Request failed with status ${response.status}`,
                    succeeded: false,
                }
            }
        } catch (error) {
            throw new Error("An unexpected error occurred while adding the checklist.")
        }
    }

    static async deleteChecklist(idChecklist: number): Promise<boolean> {
        try {
            const url = deleteChecklistUrl(idChecklist)
            const response = await HttpClient.delete(url)
            if (response.ok) {
                toast.success(t("Checklist removed"))
            } else {
                toast.error(t("Could not delete checklist"))
            }
            return response.ok
        } catch (error) {
            toast.error(t("Could not delete checklist"))
            return false
        }
    }

    static async getSchedule(idChecklist: number, idChecklistSchedule: number): Promise<ChecklistScheduleDto | undefined> {
        try {
            const url = getScheduleUrl(idChecklist, idChecklistSchedule)
            const response = await HttpClient.get(url)
            if (response.ok) {
                return await response.json()
            }
        } catch (error) {
            toast.warning(t("Could not fetch Checklist Schedule"))
        }
    }

    static async scheduleChecklist(request: ScheduleChecklistRequest): Promise<boolean> {
        try {
            const response = await HttpClient.post(scheduleChecklistUrl, {
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(request)
            })
            if (response.ok) {
                toast.success(t("Checklist scheduled successfully"))
            } else {
                toast.error(await response.text() || t("Could not schedule checklist"))
            }
            return response.ok
        } catch (error) {
            throw new Error("An unexpected error occurred while scheduling the checklist.")
        }
    }

    static async updateChecklistSchedule(request: ScheduleChecklistRequest): Promise<boolean> {
        try {
            const requestBody = JSON.stringify(request)

            const response = await HttpClient.put(scheduleChecklistUrl, {
                headers: { 'Content-Type': 'application/json' },
                body: requestBody
            })

            if (response.ok) {
                toast.success(t("Checklist schedule updated successfully"))
            } else {
                toast.error(t("Could not update checklist schedule"))
            }

            return response.ok
        } catch (error) {
            throw new Error("An unexpected error occurred while scheduling the checklist.")
        }
    }

    static async deleteSchedule(appointmentId: number): Promise<boolean> {
        try {
            const url = deleteScheduleUrl(appointmentId.toString())
            const response = await HttpClient.delete(url)
            if (response.ok) {
                toast.success(t("Appointment deleted successfully"))
            } else {
                toast.error(t("Could not delete appointment"))
            }
            return response.ok
        } catch (error) {
            toast.error(t("An unexpected error occurred while deleting the appointment."))
            return false
        }
    }
}

export interface ScheduleChecklistRequest {
    idChecklistSchedule?: number
    idChecklist: number
    type: ScheduleType

    startDate?: string
    endDate?: string
    onceDate?: string

    dailyFrequency: number[]
    weeklyFrequency?: number

    monthlyFrequencyMonth: number[]
    monthlyFrequencyDay: number[]

    monthlyFrequencyOn: number[]
    monthlyFrequencyOnDay: number[]
}
